<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Detalle — {{ nombreCaja || '...' }}</h1>
          <p class="view-header__subtitle">Movimientos y operaciones de la caja.</p>
        </div>
        <div class="view-header__actions">
          <button type="button" class="btn view-header__btn" @click="goBack">
            <i class="bi bi-arrow-left"></i>
            Volver
          </button>
          <v-guard :permissions="['CAJAS_HACER_MOVIMIENTOS']">
            <button type="button" class="btn view-header__btn" @click="setBalance">
              <i class="bi bi-arrow-down-square"></i>
              Vaciar caja
            </button>
            <button type="button" class="btn view-header__btn" @click="openTransModal">
              <i class="bi bi-arrow-left-right"></i>
              Nueva transferencia
            </button>
            <button type="button" class="btn view-header__btn view-header__btn--primary" @click="openMovementModal">
              <i class="bi bi-arrow-down-up"></i>
              Nuevo movimiento
            </button>
          </v-guard>
        </div>
      </div>
    </div>

    <!-- BALANCE -->
    <div class="stats-card">
      <div class="stats-card__value" :class="{ 'stats-card__value--danger': movementsTotal < 0 }">
        <template v-if="currency === 'US$'">{{ formatoAme2Dec(movementsTotal) }}</template>
        <template v-else>{{ formatoMoneda2Dec(movementsTotal) }}</template>
      </div>
      <div class="stats-card__label">Saldo actual</div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar movimiento...">
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
      />
    </div>

    <!-- MODAL NUEVO MOVIMIENTO -->
    <NuevoMovimientoCaja
      v-if="isModalVisible"
      :fundId="idCaja"
      :balance="movementsTotal"
      @confirmsave="onModalSuccess"
      @close="closeMovementModal"
    />

    <!-- MODAL TRANSFERENCIA -->
    <TransferenciaFondos
      v-if="isModalVisibleTrans"
      :fundId="idCaja"
      @confirmsave="onModalSuccess"
      @close="closeTransModal"
    />
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import NuevoMovimientoCaja from "./../sub-components/Nuevo-movimiento-caja.vue";
import TransferenciaFondos from "./../sub-components/Transferencia-fondos.vue";
import formatDateMixin from "../../mixins/formatDateMixin";
import formatMoneyMixin from "./../../mixins/formatMoneyMixin";

export default {
  components: {
    VdtnetTable,
    NuevoMovimientoCaja,
    TransferenciaFondos,
  },
  mixins: [formatDateMixin, formatMoneyMixin],
  data: function () {
    return {
      isModalVisible: false,
      isModalVisibleTrans: false,
      doSearch: "",
      isLoading: false,
      movementsTotal: 0,
      nombreCaja: "",
      currency: "",
      // Solo las 4 props que usa setBalance(); el shape de movement
      // que el endpoint /api/funds/movement consume.
      movement: {
        type: "out",
        amount: null,
        category_id: null,
        description: "",
      },
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/funds/getMovements/" + this.$route.params.idCaja,
          type: "GET",
          data: (d) => d,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        processing: false,
        pageLength: 15,
        searching: true,
        searchDelay: 500,
        destroy: true,
        order: [[0, 'desc']],
        ordering: true,
        lengthChange: true,
        serverSide: true,
        autoWidth: false,
      },
      fields: {
        id: { visible: false },
        created_at: {
          label: "Fecha",
          width: "150px",
          searchable: true,
          render: (data) => this.formatoFechaHora(data),
        },
        notes: { label: "Descripción", searchable: true },
        category: { label: "Categoría", searchable: true, width: "150px" },
        origen: { label: "Orígen", searchable: true, width: "150px" },
        in: {
          label: "Ingresos",
          width: "130px",
          align: "right",
          render: (data, type) => {
            if (type !== 'display') return data;
            const formatted = this.formatCurrencyArr(data);
            return formatted ? `<span class="text-success">${formatted}</span>` : '';
          },
        },
        out: {
          label: "Egresos",
          width: "130px",
          align: "right",
          render: (data, type) => {
            if (type !== 'display') return data;
            const formatted = this.formatCurrencyArr(data);
            return formatted ? `<span class="text-danger">${formatted}</span>` : '';
          },
        },
        balance: {
          label: "Saldo",
          width: "140px",
          align: "right",
          render: (data, type) => {
            if (type !== 'display') return data;
            // Cuando hay búsqueda activa el saldo acumulado pierde sentido.
            if (this.doSearch !== "") return '';
            const formatted = this.formatCurrencyArr(data, true);
            const value = Array.isArray(data) ? Number(data[1]) : 0;
            const colorClass = value >= 0 ? 'text-success' : 'text-danger';
            return `<span class="data-table__primary ${colorClass}">${formatted}</span>`;
          },
        },
      },
    };
  },
  computed: {
    idCaja() {
      return this.$route.params.idCaja;
    },
  },
  mounted() {
    this.getTotal();
    this.getFund();
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  watch: {
    doSearch(newValue, oldValue) {
      if (newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch);
      }
      if (newValue === "" && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch);
      }
    },
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'Cajas' });
    },
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
      this.getTotal();
    },
    getTotal() {
      this.$http.get("/api/funds/getBalance/" + this.idCaja).then((response) => {
        this.movementsTotal = response.data.data;
      });
    },
    getFund() {
      this.$http.get("/api/funds/getFund/" + this.idCaja).then((response) => {
        if (response.status === 200 && response.data && response.data.data) {
          this.nombreCaja = response.data.data.name;
          this.currency = response.data.data.currency;
        }
      });
    },
    setBalance() {
      if (this.movementsTotal === 0) return;
      if (!confirm("¿Está seguro que desea vaciar esta caja?")) return;
      const isPositive = this.movementsTotal > 0;
      this.movement.amount = Math.abs(this.movementsTotal);
      this.movement.category_id = isPositive ? 6 : 7;
      this.movement.description = "Vaciar Caja";
      this.movement.type = isPositive ? "out" : "in";
      this.$http.post("/api/funds/movement/" + this.idCaja, this.movement).then(() => {
        this.refreshTable();
      });
    },
    openMovementModal() {
      this.isModalVisible = true;
    },
    closeMovementModal() {
      this.isModalVisible = false;
    },
    openTransModal() {
      this.isModalVisibleTrans = true;
    },
    closeTransModal() {
      this.isModalVisibleTrans = false;
    },
    onModalSuccess() {
      this.refreshTable();
    },
    formatCurrencyArr(arr, fillZero = false) {
      if (!Array.isArray(arr)) return fillZero ? '$ 0,00' : '';
      const isPesos = arr[0] === '$';
      const num = isPesos
        ? $.fn.dataTable.render.number(".", ",", 2).display(arr[1])
        : $.fn.dataTable.render.number(",", ".", 2).display(arr[1]);
      if (!num && !fillZero) return '';
      const display = num || (isPesos ? '0,00' : '0.00');
      return `${isPesos ? '$' : 'US$'} ${display}`;
    },
  },
};
</script>
